import { useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { FeatureMappingRow } from '../data-qa-types';
import { formatCurrency } from '../data-qa-utils';

interface SankeyNode {
  id: string;
  label: string;
  tier: number;
  value: number;
  y: number;
  height: number;
  // MMM Feature tier (3) only -- every distinct raw feature_id backing this node,
  // split back out of FeatureMappingRow.featureId's own comma-join (see its doc
  // comment) so aggregating rows into one node here can't accidentally concatenate an
  // already-joined string with itself. Empty for tiers 0-2, which have no feature id.
  featureIds: Set<string>;
}

interface SankeyLink {
  sourceId: string;
  targetId: string;
  value: number;
}

interface SankeyChartProps {
  rows: FeatureMappingRow[];
  height?: number;
}

// Blue / orange / green / purple column tiers, matching the Streamlit prototype
// (data-viz category colors, not UI-chrome tokens).
const TIER_LABELS = ['Channels', 'Platforms', 'Tactics', 'MMM Features'];
const TIER_COLORS = ['#4a90d9', '#ef8c34', '#37c28f', '#9061c2'];
const NODE_WIDTH = 16;
const NODE_GAP = 8;
const PADDING_X = 16;
const PADDING_Y = 16;
const HEADER_H = 30;
const HEADER_GAP = 16;
const HEADER_BAR_GAP = 8;
// Every node gets at least this tall a bar regardless of its share of that tier's
// total value (see buildGraph below) -- otherwise a low-value node (common for
// "None" categories) would be invisible.
const MIN_NODE_HEIGHT = 8;

// The value itself, not a path -- a node represents one distinct category value per
// tier (e.g. a single shared "None" node at the Tactics tier), not one node per
// (channel, platform, tactic) combination. Multiple upstream nodes can then fan into
// the same shared node and it can fan back out to multiple downstream nodes, same as
// any standard Sankey with a shared/uncategorized bucket. The caller prefixes this with
// the tier number to build a node id, so a tier-0 "None" and a tier-2 "None" never
// collide despite sharing this same raw value.
const tierKey = (row: FeatureMappingRow, tier: number): string => {
  if (tier === 0) return row.channel;
  if (tier === 1) return row.platform;
  if (tier === 2) return row.tactic;
  return row.feature;
};

// Water-filling allocation: give each node its proportional share of valueHeight, but
// never less than MIN_NODE_HEIGHT -- and unlike a plain per-node
// Math.max(MIN_NODE_HEIGHT, proportional), the height "given away" to under-share nodes
// is reclaimed from the remaining nodes' own shares rather than added on top, so the
// total NEVER exceeds valueHeight regardless of how skewed the tier's value
// distribution is. Confirmed against real `away` Feature Mapping data (2026-08-25) that
// the naive per-node floor overflows: a tier with one dominant node and many near-zero
// "None" nodes had the dominant node keep its full undiminished share WHILE the small
// ones were each bumped up to the floor, so the sum exceeded the container -- clipping
// the last node. Iterative because flooring a node changes the remaining pool's own
// total, which can push another node below the floor too (classic bandwidth/water-
// filling allocation).
const allocateNodeHeights = (nodes: SankeyNode[], valueHeight: number): void => {
  const remaining = new Set(nodes);
  let budget = valueHeight;
  for (;;) {
    if (remaining.size === 0) break;
    const totalValue = [...remaining].reduce((s, n) => s + n.value, 0);
    let flooredAny = false;
    for (const node of remaining) {
      const share = totalValue > 0 ? (node.value / totalValue) * budget : budget / remaining.size;
      if (share <= MIN_NODE_HEIGHT) {
        node.height = MIN_NODE_HEIGHT;
        budget -= MIN_NODE_HEIGHT;
        remaining.delete(node);
        flooredAny = true;
      }
    }
    if (!flooredAny) {
      // Same zero-guard as `share` above -- if every remaining node has value 0 (e.g.
      // a channel/platform group whose feature-mapping rows all sum to $0 spend),
      // totalValue is 0 and node.value / totalValue is 0/0 = NaN, poisoning every
      // node's height (and, via `cursor` below, every following node's y position too).
      for (const node of remaining) {
        node.height = totalValue > 0 ? (node.value / totalValue) * budget : budget / remaining.size;
      }
      break;
    }
  }
};

const buildGraph = (rows: FeatureMappingRow[], chartHeight: number) => {
  const tiers: Record<number, Map<string, SankeyNode>> = {
    0: new Map(),
    1: new Map(),
    2: new Map(),
    3: new Map()
  };
  const linkMap = new Map<string, SankeyLink>();

  for (const row of rows) {
    for (let tier = 0; tier < 4; tier += 1) {
      const id = `${tier}:${tierKey(row, tier)}`;
      const value = row.spend;
      let node = tiers[tier].get(id);
      if (!node) {
        node = {
          id,
          label: tierKey(row, tier),
          tier,
          value: 0,
          y: 0,
          height: 0,
          featureIds: new Set()
        };
        tiers[tier].set(id, node);
      }
      node.value += value;
      if (tier === 3 && row.featureId) {
        row.featureId.split(', ').forEach(fid => node.featureIds.add(fid));
      }

      if (tier < 3) {
        const nextId = `${tier + 1}:${tierKey(row, tier + 1)}`;
        const key = `${id}→${nextId}`;
        const link = linkMap.get(key);
        if (link) {
          link.value += value;
        } else {
          linkMap.set(key, { sourceId: id, targetId: nextId, value });
        }
      }
    }
  }

  const usableHeight = chartHeight - PADDING_Y * 2;

  for (let tier = 0; tier < 4; tier += 1) {
    const nodes = [...tiers[tier].values()].sort((a, b) => b.value - a.value);
    const gapTotal = Math.max(0, (nodes.length - 1) * NODE_GAP);
    const valueHeight = usableHeight - gapTotal;
    allocateNodeHeights(nodes, valueHeight);
    let cursor = PADDING_Y;
    for (const node of nodes) {
      node.y = cursor;
      cursor += node.height + NODE_GAP;
    }
  }

  return {
    nodes: Object.values(tiers).flatMap(map => [...map.values()]),
    links: [...linkMap.values()]
  };
};

const buildLinkPath = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  thickness: number
) => {
  const curvature = 0.5;
  const xi = sourceX + (targetX - sourceX) * curvature;
  const xj = sourceX + (targetX - sourceX) * (1 - curvature);
  const halfT = thickness / 2;
  return [
    `M${sourceX},${sourceY - halfT}`,
    `C${xi},${sourceY - halfT} ${xj},${targetY - halfT} ${targetX},${targetY - halfT}`,
    `L${targetX},${targetY + halfT}`,
    `C${xj},${targetY + halfT} ${xi},${sourceY + halfT} ${sourceX},${sourceY + halfT}`,
    'Z'
  ].join(' ');
};

const layoutSankey = (rows: FeatureMappingRow[], chartWidth: number, chartHeight: number) => {
  const { nodes, links } = buildGraph(rows, chartHeight);
  const usableWidth = chartWidth - PADDING_X * 2;
  const tierXs = Array.from(
    { length: 4 },
    (_, i) => PADDING_X + (usableWidth / 3) * i - (i === 3 ? NODE_WIDTH : 0)
  );

  const nodeById = new Map(nodes.map(n => [n.id, n]));

  const linkSegments = links
    .map(link => {
      const source = nodeById.get(link.sourceId);
      const target = nodeById.get(link.targetId);
      if (!source || !target) return undefined;
      const sourceX = tierXs[source.tier] + NODE_WIDTH;
      const targetX = tierXs[target.tier];
      const sourceY = source.y + source.height / 2;
      const targetY = target.y + target.height / 2;
      const ratio = link.value / Math.max(source.value, 1);
      const thickness = Math.max(2, ratio * source.height);
      return {
        ...link,
        path: buildLinkPath(sourceX, sourceY, targetX, targetY, thickness),
        sourceTier: source.tier
      };
    })
    .filter((l): l is NonNullable<typeof l> => l !== undefined);

  return { nodes, links: linkSegments, tierXs };
};

// The tallest tier (most distinct nodes -- typically MMM Features, or Platforms/Tactics
// when many carry "None") sets how much vertical space every tier actually needs once
// each node is floored to MIN_NODE_HEIGHT (see buildGraph). A fixed chart height doesn't
// know this in advance -- confirmed against real `away` Feature Mapping data (2026-08-25)
// that a fixed 480px was too short once enough low-value nodes each got floored to 8px,
// so the layout's cumulative height silently overflowed the SVG's own height, clipping
// the last (smallest) node and the link feeding it. Computed independently of chartHeight
// so SankeyChart can size its container to whichever is taller.
const computeMinChartHeight = (rows: FeatureMappingRow[]): number => {
  const maxNodeCount = Math.max(
    1,
    ...[0, 1, 2, 3].map(tier => new Set(rows.map(row => tierKey(row, tier))).size)
  );
  const usableHeight = maxNodeCount * MIN_NODE_HEIGHT + (maxNodeCount - 1) * NODE_GAP;
  return usableHeight + PADDING_Y * 2 + HEADER_H + HEADER_GAP;
};

interface HoverHighlight {
  nodeIds: Set<string>;
  linkKeys: Set<string>;
}

// MMM Feature (tier 3) nodes get the raw feature_id(s) affixed for debugging (Sean,
// 2026-09-10) -- every other tier has no feature id at all (featureIds stays empty).
const nodeTooltipText = (node: SankeyNode): string => {
  const idSuffix =
    node.tier === 3 && node.featureIds.size > 0
      ? ` (${Array.from(node.featureIds).sort().join(', ')})`
      : '';
  return `${node.label}${idSuffix}: ${formatCurrency(node.value)}`;
};

// Traces the hovered node's highlight directly from the underlying rows, NOT by
// walking the (now-deduped, see tierKey above) graph of nodes/links. A graph walk is
// wrong once nodes are shared across branches: hovering "Paid Social" would correctly
// reach its own tactics via real links, but then incorrectly continue forward from a
// shared tactic node (e.g. "None", also used by Linear/Podcast rows) into every OTHER
// channel's features too, since graph reachability can't tell which incoming edge the
// walk actually arrived by. Filtering to the rows that literally pass through the
// hovered node -- then only marking the tier values/edges those specific rows touch --
// has no such ambiguity, because it never leaves the hovered node's own data.
// Confirmed against real `away` Feature Mapping data (2026-08-25): hovering a channel
// whose "None" tactic is shared with other channels no longer highlights those other
// channels' features.
const computeHoverHighlight = (
  hoveredNodeId: string,
  nodes: SankeyNode[],
  rows: FeatureMappingRow[]
): HoverHighlight => {
  const hoveredNode = nodes.find(n => n.id === hoveredNodeId);
  if (!hoveredNode) return { nodeIds: new Set([hoveredNodeId]), linkKeys: new Set() };

  const matchingRows = rows.filter(row => tierKey(row, hoveredNode.tier) === hoveredNode.label);

  const nodeIds = new Set<string>();
  const linkKeys = new Set<string>();
  for (const row of matchingRows) {
    const tierIds = [0, 1, 2, 3].map(tier => `${tier}:${tierKey(row, tier)}`);
    tierIds.forEach(id => nodeIds.add(id));
    for (let tier = 0; tier < 3; tier += 1) {
      linkKeys.add(`${tierIds[tier]}→${tierIds[tier + 1]}`);
    }
  }
  return { nodeIds, linkKeys };
};

interface SankeyBodyProps {
  rows: FeatureMappingRow[];
  width: number;
  height: number;
}

const SankeyBody = ({ rows, width, height }: SankeyBodyProps) => {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>();
  const headerOffset = HEADER_H + HEADER_GAP;
  const bodyHeight = Math.max(0, height - headerOffset);
  const { nodes, links, tierXs } = useMemo(
    () => layoutSankey(rows, width, bodyHeight),
    [rows, width, bodyHeight]
  );
  const hoverHighlight = useMemo(
    () => (hoveredNodeId ? computeHoverHighlight(hoveredNodeId, nodes, rows) : undefined),
    [hoveredNodeId, nodes, rows]
  );

  const usableWidth = width - PADDING_X * 2;
  const barWidth = (usableWidth - HEADER_BAR_GAP * 3) / 4;

  return (
    <svg width={width} height={height} className='data-qa-sankey__svg'>
      <g className='data-qa-sankey__tier-headers'>
        {TIER_LABELS.map((label, i) => {
          const x = PADDING_X + i * (barWidth + HEADER_BAR_GAP);
          return (
            <g key={label}>
              <rect x={x} y={0} width={barWidth} height={HEADER_H} rx={6} fill={TIER_COLORS[i]} />
              <text
                x={x + barWidth / 2}
                y={HEADER_H / 2 + 4}
                textAnchor='middle'
                className='data-qa-sankey__tier-label'
              >
                {label}
              </text>
            </g>
          );
        })}
      </g>
      <g transform={`translate(0, ${headerOffset})`}>
        <g className='data-qa-sankey__links'>
          {links.map(link => (
            <path
              key={`${link.sourceId}→${link.targetId}`}
              d={link.path}
              fill={TIER_COLORS[link.sourceTier]}
              fillOpacity={
                hoverHighlight && !hoverHighlight.linkKeys.has(`${link.sourceId}→${link.targetId}`)
                  ? 0.08
                  : 0.28
              }
            >
              <title>
                {nodes.find(n => n.id === link.sourceId)?.label} →{' '}
                {nodes.find(n => n.id === link.targetId)?.label}: {formatCurrency(link.value)}
              </title>
            </path>
          ))}
        </g>
        <g className='data-qa-sankey__nodes'>
          {nodes.map(node => (
            <g
              key={node.id}
              transform={`translate(${tierXs[node.tier]}, ${node.y})`}
              opacity={hoverHighlight && !hoverHighlight.nodeIds.has(node.id) ? 0.35 : 1}
              onMouseEnter={() => setHoveredNodeId(node.id)}
              onMouseLeave={() => setHoveredNodeId(undefined)}
            >
              <rect width={NODE_WIDTH} height={node.height} fill={TIER_COLORS[node.tier]} rx={2} />
              <text
                x={node.tier === 3 ? -6 : NODE_WIDTH + 6}
                y={node.height / 2 + 4}
                textAnchor={node.tier === 3 ? 'end' : 'start'}
                className='data-qa-sankey__node-label'
              >
                {node.label}
              </text>
              <title>{nodeTooltipText(node)}</title>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
};

export const SankeyChart = ({ rows, height = 480 }: SankeyChartProps) => {
  if (rows.length === 0) {
    return <div className='data-qa-sankey data-qa-sankey--empty'>No mapping rows to display.</div>;
  }

  const totalHeight = Math.max(height, computeMinChartHeight(rows));

  return (
    <div className='data-qa-sankey' style={{ height: totalHeight }}>
      <AutoSizer
        renderProp={({ width, height: h }) => (
          <SankeyBody rows={rows} width={width ?? 0} height={h ?? 0} />
        )}
      />
    </div>
  );
};
